// shared/utils/dataSourceLinkingUtils.js
import logger from '../services/logging/logger';

/**
 * Adds linking columns (Run, Test_Case_Id, Response_Id, Step_Flow) to request CSV content.
 * This ensures the same linking behavior as the manual test suite data source linking
 * in DataSourceService.setPrimaryDataSource().
 *
 * Column behavior:
 * - Run: defaults to 'true' if not present
 * - Test_Case_Id: preserved from original CSV
 * - Response_Id: calculated as (rowIndex % responseRowCount) + 1, cycling through response rows
 * - Step_Flow: defaults to 'default' if not present
 *
 * @param {string} requestCsvContent - Raw CSV string for request data
 * @param {string} responseCsvContent - Raw CSV string for response data (used to calculate Response_Id)
 * @returns {string} Modified request CSV with linking columns added
 */
export function addLinkingColumnsToRequestCSV(requestCsvContent, responseCsvContent) {
  if (!requestCsvContent) return requestCsvContent;

  try {
    const requestRows = parseCsvRows(requestCsvContent);
    const responseRows = responseCsvContent ? parseCsvRows(responseCsvContent) : [];

    if (requestRows.length < 2) {
      // Need at least header + 1 data row
      logger.debug('Request CSV has less than 2 rows, skipping linking columns');
      return requestCsvContent;
    }

    const requestHeaders = requestRows[0].map(h => h.trim().replace(/^"|"$/g, ''));
    const responseRowCount = Math.max(responseRows.length - 1, 0); // Exclude header row

    logger.debug('Adding linking columns to request CSV', {
      requestRowCount: requestRows.length - 1,
      responseRowCount,
      existingHeaders: requestHeaders
    });

    // Collect headers that are NOT linking columns (they'll be placed after the linking columns)
    const linkingColumnNames = ['Run', 'Test_Case_Id', 'Response_Id', 'Step_Flow'];
    const otherHeaders = requestHeaders.filter(h => !linkingColumnNames.includes(h));

    // Build new header: Run, Test_Case_Id, Response_Id, Step_Flow, ...remaining columns
    const newHeaders = [...linkingColumnNames, ...otherHeaders];

    // Build new data rows
    const newDataRows = [];
    for (let i = 1; i < requestRows.length; i++) {
      const cells = requestRows[i];
      // Map original headers to cell values
      const rowObj = {};
      requestHeaders.forEach((header, idx) => {
        rowObj[header] = (cells[idx] || '').trim().replace(/^"|"$/g, '');
      });

      // Calculate Response_Id: cycle through response rows (1-based indexing)
      const dataIndex = i - 1;
      let responseId = '';
      if (responseRowCount > 0) {
        responseId = String((dataIndex % responseRowCount) + 1);
      }

      // Build the new row in linking column order, matching DataSourceService.setPrimaryDataSource logic
      const newRow = [
        rowObj['Run'] || 'true',
        rowObj['Test_Case_Id'] || String(i),
        rowObj['Response_Id'] || responseId,
        rowObj['Step_Flow'] || 'default',
        ...otherHeaders.map(h => rowObj[h] || '')
      ];

      newDataRows.push(newRow);
    }

    // Convert back to CSV string
    const csvLines = [
      newHeaders.join(','),
      ...newDataRows.map(row => row.map(cell => escapeCsvCell(cell)).join(','))
    ];

    const result = csvLines.join('\n');
    logger.info('Linking columns added to request CSV', {
      originalColumns: requestHeaders.length,
      newColumns: newHeaders.length,
      dataRows: newDataRows.length,
      responseRowCount
    });

    return result;
  } catch (error) {
    logger.error('Error adding linking columns to CSV', { error: error.message });
    return requestCsvContent; // Return original on error
  }
}

/**
 * Parse CSV content into rows of cell arrays.
 * Handles quoted fields with commas inside them.
 *
 * @param {string} csvString - Raw CSV string
 * @returns {Array<Array<string>>} Array of rows, each row is an array of cell values
 */
function parseCsvRows(csvString) {
  const lines = csvString.trim().split('\n');
  return lines.filter(line => line.trim()).map(line => parseCsvLine(line));
}

/**
 * Parse a single CSV line respecting quoted fields.
 *
 * @param {string} line - A single line of CSV
 * @returns {Array<string>} Array of cell values
 */
function parseCsvLine(line) {
  const result = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += char;
    }
  }

  result.push(current);
  return result;
}

/**
 * Escape a CSV cell value (wrap in quotes if contains comma, quote, or newline).
 *
 * @param {string} value - Cell value to escape
 * @returns {string} Escaped cell value
 */
function escapeCsvCell(value) {
  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}
